import { useNavigation } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import dayjs from 'dayjs';
import { Field, Formik, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScrollView,
  TouchableOpacity,
  View,
  StyleSheet,
  Animated,
  TextInput,
} from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import { Alert } from '@components/Alert';
import Box from '@components/Box/Box';
import { AddMembersSection } from '@components/Common/AddMembersSection.web';
import { CustomToast } from '@components/CustomToast/CustomToast';
import { TeamsModal } from '@components/FormModals/TeamsModal';
import { CancelConfirmTaskAndProjectModal } from '@components/Modals/CancelConfirmTaskAndProjectModal';
import { WebModalV3 } from '@components/Modals/WebModalV3.web';
import { resolveEditModeMembers } from '@components/Projects/projectFormMemberPayload';
import { FormValues } from '@components/Projects/ProjectFormStack';
import { ProjectInviteOrAddMember } from '@components/Projects/ProjectInviteOrAddMember.web';
import { Text } from '@components/Restyle/index';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import ListNavigationItem from '@components/shared/ListNavigationItem/ListNavigationItem';
import TextField from '@components/shared/TextField/TextField';
import { EditTaskDescription } from '@components/Tasks/EditTaskDescription';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { SLIDE_IN_PANEL_DURATION_MS } from '@components/Web/Navigator/SlideInPanel.web';
import { TooltipOnboardingWeb } from '@components/Web/TooltipOnboarding.web';
import {
  ListTeamsDocument,
  ListTeamsQuery,
  LocalFile,
  ProjectMemberRole,
  Team,
  TeamMemberRole,
  useCreateTeamMutation,
  useListProductsQuery,
  useUpdateOnboardingStateMutation,
} from '@graphql/generated';
import useFileProcessor from '@hooks/useFileProcessor';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';
import useMe from '@hooks/useMe';
import useNewColors, { ColorType } from '@hooks/useNewColors';
import { useOnboardingActive } from '@hooks/useOnboardingActive';
import { useProjectTutorialWeb } from '@hooks/useProjectTutorialWeb';
import useSelectedMembers from '@hooks/useSelectedMembers';
import useTabFocus from '@hooks/useTabFocus';
import { useTeamBillingFromQuery } from '@hooks/useTeamBillingFromQuery';
import { useTeamDetailFromQuery } from '@hooks/useTeamDetailFromQuery';
import { createTeamSchema } from '@schemas/createTeamSchema';
import {
  PROJECT_NAME_LENGTH_LIMIT,
  PROJECT_DESC_LENGTH_LIMIT,
} from '@src/constants/constants';
import Images from '@themes/images';
import theme from '@themes/theme';
import { canUpdateAdminRole, getProjectRoleEnumValue } from '@utils/projects';
import { AsyncStorage } from '@utils/storage';
import { submitFormIgnoringRejection } from '@utils/submitFormHandlers';
import {
  hasReachedPersonalProjectLimit,
  shouldShowPaywall,
} from '@utils/teams/paywall';
import { uploadTeamLogo } from '@utils/teams/uploadTeamLogo';

import type { FormValues as TeamFormValues } from '@navigation/teams/create-team-stack';

type ProjectFormProps = {
  screenName?: string;
  // TA-10949 #24: lets the panel's X (rendered outside this Formik tree, see
  // MainPanel.web.tsx) ask this form "is it safe to close?" instead of
  // closing unconditionally — mirrors TaskForm.web.tsx's own version.
  attemptCloseRef?: React.MutableRefObject<(() => void) | null>;
};

export type ProjectLimitNextTime = {
  userId: string;
  teamId: string;
  nextTime: string;
};

export const getShowProjectLimitSnackBarNextTime = async () => {
  const jsonValue = await AsyncStorage.getItem(
    'showProjectLimitSnackBarNextTime'
  );
  return jsonValue !== null
    ? (JSON.parse(jsonValue) as ProjectLimitNextTime[])
    : [];
};

export const removeProjectLimitSnackBarNextTime = async (
  userId: string | undefined,
  teamId: string | undefined
) => {
  if (!userId || teamId === undefined) return;
  const savedTimes = await getShowProjectLimitSnackBarNextTime();
  const saveTimes = savedTimes?.filter((item) => {
    if (
      item.userId === userId &&
      (item.teamId === teamId || item.teamId === '0')
    ) {
      return false;
    } else {
      return true;
    }
  });
  await AsyncStorage.setItem(
    'showProjectLimitSnackBarNextTime',
    JSON.stringify(saveTimes)
  );
};

export const saveShowProjectLimitSnackBarNextTime = async (
  userId: string | undefined,
  teamId: string | undefined
) => {
  if (!userId || teamId === undefined) return;
  const savedTimes = await getShowProjectLimitSnackBarNextTime();
  const saveValue: ProjectLimitNextTime = {
    userId: userId,
    teamId: teamId,
    nextTime: dayjs(Date()).add(14, 'day').toString(),
  };
  let savedValue = false;
  const saveTimes = savedTimes?.map((item) => {
    if (item.userId === userId && item.teamId === teamId) {
      savedValue = true;
      return saveValue;
    } else {
      return item;
    }
  });
  await AsyncStorage.setItem(
    'showProjectLimitSnackBarNextTime',
    JSON.stringify(savedValue ? saveTimes : [...saveTimes, saveValue])
  );
};

const ProjectForm: React.FC<ProjectFormProps> = ({
  screenName,
  attemptCloseRef,
}) => {
  const { showOverlay, projectTutorialWebState } = useProjectTutorialWeb();
  const { me } = useMe();
  const onboardingActive = useOnboardingActive();
  const navigation = useNavigation();
  const { t } = useTranslation();
  const navigator = useNavigation();
  const { teams } = useListTeamsFromQuery();
  const primaryTeam = teams[0];
  const { colors } = useNewColors();
  const [_showProjectLimitSnackBar, setShowProjectLimitSnackBar] =
    useState(false);
  const [_hideProjectLimitSnackBar, _setHideProjectLimitSnackBar] =
    useState(false);
  const [overrideSaveAlert, setOverrideSaveAlert] = useState(false);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const { tabFocus } = useTabFocus();
  const {
    setIsCreateProjectOpen,
    setPaywallModalOpen,
    setPaywallTeam,
    setEditProjectUUId,
    setIsTaskWebPanelOpen,
    setIdTaskDetailOpen,
    setIdProjectOfTaskDetailOpen,
    setIsTaskWebPanelMaximize,
    setUpgradeTeamPlanOpen,
    setIsTeamPersonal,
    setIdTeamOpen,
    setUUIdTeamOpen,
    setTeamValues,
    setIsNewTeam,
    setUpgradeCTA,
    setUpgradeConfirmModalOpen,
  } = useWebDrawer();

  const nameRef = useRef<TextInput | null>(null);
  const [touchedTeam, setTouchedTeam] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  // TA-10891 #14: the name error stays hidden until Create/Update is pressed.
  const [nameSubmitAttempted, setNameSubmitAttempted] = useState(false);
  const [isCancelHovered, setIsCancelHovered] = useState(false);
  // Color/Icon pickers rest on grey01 — hover one step deeper to grey02
  // (TA-9005).
  const [isColorHovered, setIsColorHovered] = useState(false);
  const [isIconHovered, setIsIconHovered] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const { selectedMembers, setSelectedMembers } = useSelectedMembers();
  const {
    values,
    touched,
    submitForm,
    setFieldValue,
    setFieldTouched,
    isSubmitting,
    initialValues: formikInitialValues,
  } = useFormikContext<FormValues>();
  const [createTeam] = useCreateTeamMutation({
    update(cache, { data }) {
      const newTeam = data?.createTeam;
      if (!newTeam?.id) return;
      for (const variables of [
        {},
        { includePending: false },
        { includePending: true },
      ]) {
        const existing = cache.readQuery<ListTeamsQuery>({
          query: ListTeamsDocument,
          variables,
        });
        if (!existing) continue;
        cache.writeQuery<ListTeamsQuery>({
          query: ListTeamsDocument,
          variables,
          data: {
            listTeams: [
              ...existing.listTeams,
              {
                ...(newTeam as ListTeamsQuery['listTeams'][0]),
                hasProjects: false,
              },
            ],
          },
        });
      }
    },
  });
  const { processFiles } = useFileProcessor();
  const { data: listProductsData } = useListProductsQuery();
  const getProductPlus = listProductsData?.listProducts?.find((product) =>
    product?.name?.toUpperCase()?.includes('PLUS')
  );
  const getProductTeam = listProductsData?.listProducts?.find((product) =>
    product?.name?.toUpperCase()?.includes('TEAM')
  );

  const toast = useToast();
  const {
    teamId = '',
    users,
    color,
    icon,
    id: projectId,
    uuid: projectUUId,
    location,
    currentTeam,
  } = values;

  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const nameTooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  useEffect(
    () => () => {
      if (nameTooltipTimerRef.current)
        clearTimeout(nameTooltipTimerRef.current);
    },
    []
  );
  const [updateOnboardingState] = useUpdateOnboardingStateMutation();
  useEffect(() => {
    if (
      onboardingActive &&
      me?.onboardingState?.welcomeModalSeen === true &&
      me?.onboardingState?.projectCreationComplete === false
    ) {
      setTimeout(() => nameRef.current?.focus(), 350);
      // The tooltip measures its anchor when it opens; wait for the Create
      // Project panel's push-in to land or it pins to the mid-slide position.
      // Not cleared by this effect's cleanup: the onboarding-state write
      // below re-runs the effect within a few ms.
      nameTooltipTimerRef.current = setTimeout(
        () => setShowNameTooltip(true),
        SLIDE_IN_PANEL_DURATION_MS
      );
      setFieldValue('isOnboardingProject', true);
      const timer = setTimeout(() => {
        updateOnboardingState({
          variables: {
            attributes: {
              welcomeModalSeen: true,
              projectCreationComplete: true,
            },
          },
          update: (cache) => {
            if (!me?.id) return;
            cache.modify({
              id: cache.identify({ __typename: 'User', id: me.id }),
              fields: {
                onboardingState: (existing) => ({
                  ...existing,
                  welcomeModalSeen: true,
                  projectCreationComplete: true,
                }),
              },
            });
          },
        });
      }, 5);
      return () => clearTimeout(timer);
    }
  }, [
    onboardingActive,
    me?.onboardingState?.welcomeModalSeen,
    me?.onboardingState?.projectCreationComplete,
  ]);
  const tooltipOpacity = useRef(new Animated.Value(0)).current;
  const fadeStarted = useRef(false);

  useEffect(() => {
    if (showNameTooltip && values.name.length === 0) {
      Animated.timing(tooltipOpacity, {
        toValue: 1,
        duration: 300,
        delay: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [showNameTooltip]);
  // Fade out tooltip immediately over 4 seconds when typing (triggered only once)
  useEffect(() => {
    if (values.name.length > 0 && showNameTooltip && !fadeStarted.current) {
      fadeStarted.current = true;
      Animated.timing(tooltipOpacity, {
        toValue: 0,
        duration: 3000, // fade out over 3 seconds
        useNativeDriver: true,
      }).start(() => setShowNameTooltip(false));
    }
  }, [values.name.length, showNameTooltip, tooltipOpacity]);

  const sortedUsers = [...users].sort((u) =>
    u.role !== ProjectMemberRole.Owner ? 1 : -1
  );

  const selectedTeam = teams.find((item) => item.id === teamId);
  const { name: selectedTeamName } = selectedTeam || {};
  // TA-9872: subscriptionDetails was split out of listTeams — read it from
  // the getTeamBilling query instead.
  const { billingByTeamId } = useTeamBillingFromQuery();
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [isOwnerOfTeam, setIsOwnerOfTeam] = useState(false);

  const selectedColor = colors.find((item: ColorType) => item.id === color);

  const updateMember = (userId: string, role: string) => {
    const members = users?.map((u) => {
      if (u.id !== userId) return u;
      else {
        return { ...u, role: getProjectRoleEnumValue(role) };
      }
    });
    setFieldTouched('users');
    setFieldValue('users', [...members]);
    setSelectedMembers(members);
  };
  // getPublicTeam, not getTeam (TA-10850): getTeam answers "The requested team
  // could not be found" for a viewer who is not a member of the team yet.
  // withMembers: false (TA-10847): only TeamDetail fields are read here.
  const { fullTeam: team, loading } = useTeamDetailFromQuery({
    teamUUId: selectedTeam?.uuid,
    withMembers: false,
  });

  // TA-9872: prefer the authoritative subscriptionDetails from getTeam
  // (TeamDetail still selects subscriptionDetails and is already fetched for
  // the selected team), falling back to the billing query only while getTeam is loading or
  // hasn't caught up to the current selection. This avoids the window where a
  // billing-merge would read subscriptionDetails as undefined and let an
  // expired team skip the paywall.
  const selectedTeamSubscriptionDetails =
    (team?.uuid === selectedTeam?.uuid
      ? team?.subscriptionDetails
      : undefined) ??
    billingByTeamId[selectedTeam?.id ?? '']?.subscriptionDetails;
  const selectedTeamWithBilling = selectedTeam && {
    ...selectedTeam,
    subscriptionDetails: selectedTeamSubscriptionDetails,
  };

  const saveAlert = () => {
    Alert.alert(
      t('models:saveProject.title'),
      t('models:saveProject.message'),
      [
        {
          text: t('shared:cancel'),
        },
        {
          text: t('shared:leavePage'),

          onPress: () => {
            setOverrideSaveAlert(true);

            // debounce, else alert will show twice
            setTimeout(() => {
              navigation.goBack();
            }, 10);
          },
        },
      ]
    );
  };
  const onPressCancel = useCallback(() => {
    if (!teamId) {
      setFieldValue('teamId', primaryTeam?.id);
    }
    setIsCancelModalVisible(false);
    setIsCreateProjectOpen(false);
    setEditProjectUUId('');
    if (projectUUId && tabFocus === 'Projects') {
      navigation.navigate('projects-stack', {
        screen: 'details',
        params: { projectUUId },
      });
    } else {
      navigation.navigate('allprojects');
    }
  }, [
    teamId,
    primaryTeam,
    setFieldValue,
    setIsCreateProjectOpen,
    setEditProjectUUId,
    projectUUId,
    tabFocus,
    navigation,
  ]);

  // TA-10949 #24 (Copilot review round 1): the auto-default effect below can
  // reassign teamId AFTER this component (and this ref) mounts, so
  // formikInitialValues.teamId alone can't be the dirty baseline for Create —
  // it starts '' and the effect's own assignment would then read as a user
  // edit. Track the "non-user" baseline in a ref instead: it starts at the
  // form's real initial teamId (Edit) and is bumped in lockstep whenever the
  // auto-default effect assigns a team (Create), so an auto-default never
  // counts as dirty but a later USER-driven reassignment still does, in both
  // modes.
  const teamBaselineRef = useRef(formikInitialValues.teamId);

  // TA-10949 #24/#28 (review round 2): comparing users/projectMembers by
  // `.length` missed any edit that keeps the count the same — a role change
  // via updateMember(), or removing one member while adding a different one.
  // Compare an id/email + role signature instead (mirrors the identity
  // ProjectInviteOrAddMember's own `key` prop above already uses), sorted so
  // a same-membership reorder isn't mistaken for a real edit.
  const usersSignature = (
    list?: { id?: string | null; role?: string | null }[]
  ) =>
    (list ?? [])
      .map((u) => `${u.id}-${u.role}`)
      .sort()
      .join(',');
  const projectMembersSignature = (
    list?: { email?: string | null; role?: string | null }[]
  ) =>
    (list ?? [])
      .map((m) => `${m.email}-${m.role}`)
      .sort()
      .join(',');

  // TA-10949 #24: Formik's own `dirty` isn't usable here — on Create it
  // auto-defaults teamId/teamUUId to the user's primary team as soon as
  // useListTeamsFromQuery resolves (effect below), which makes `dirty` true
  // before the user has touched anything. Compare the fields a user can
  // actually fill in instead. Location is deep-compared field by field since
  // ProjectLocationSearch always writes a fresh { address, latitude,
  // longitude } object — comparing by reference/existence would miss an
  // edited address.
  const isMeaningfullyDirty =
    values.name !== formikInitialValues.name ||
    values.description !== formikInitialValues.description ||
    values.location?.address !== formikInitialValues.location?.address ||
    values.location?.latitude !== formikInitialValues.location?.latitude ||
    values.location?.longitude !== formikInitialValues.location?.longitude ||
    usersSignature(values.users) !==
      usersSignature(formikInitialValues.users) ||
    projectMembersSignature(values.projectMembers) !==
      projectMembersSignature(formikInitialValues.projectMembers) ||
    values.color !== formikInitialValues.color ||
    values.icon !== formikInitialValues.icon ||
    values.teamId !== teamBaselineRef.current;

  // The panel's X (MainPanel.web.tsx) lives outside this Formik tree and
  // used to close unconditionally. It now calls this instead —
  // discard-confirm only when the form actually has changes, otherwise
  // close immediately, same rule as the Cancel button below.
  useEffect(() => {
    if (!attemptCloseRef) return;
    attemptCloseRef.current = () => {
      if (isMeaningfullyDirty) {
        setIsCancelModalVisible(true);
      } else {
        onPressCancel();
      }
    };
    return () => {
      attemptCloseRef.current = null;
    };
  }, [attemptCloseRef, isMeaningfullyDirty, onPressCancel]);

  useEffect(() => {
    if (selectedTeam?.uuid && values.teamUUId !== selectedTeam.uuid) {
      setFieldValue('teamUUId', selectedTeam.uuid);
    }
  }, [selectedTeam?.uuid, values.teamUUId]);

  useEffect(() => {
    if (
      team?.currentUserRole === TeamMemberRole.Owner ||
      me?.id === team?.owner?.id
    ) {
      setIsOwnerOfTeam(true);
      return;
    }
    setIsOwnerOfTeam(false);
  }, [team?.currentUserRole, team?.owner?.id, me?.id]);

  useEffect(
    () =>
      navigation.addListener('beforeRemove', (e) => {
        if (overrideSaveAlert) {
          return;
        }
        if (projectId && Object.entries(touched).length > 0) {
          e.preventDefault();
          saveAlert();
        } else {
          return;
        }
      }),
    [navigation, touched, overrideSaveAlert]
  );

  useEffect(() => {
    if (!values.teamId && primaryTeam?.id) {
      // Keep the dirty-check baseline (above) in lockstep with this
      // auto-assignment so it isn't mistaken for a user edit.
      teamBaselineRef.current = primaryTeam.id;
      setFieldValue('teamId', primaryTeam.id);
    }
  }, [primaryTeam, values.teamId, setFieldValue]);

  const [isExpired, setIsExpired] = useState(
    new Date(selectedTeamSubscriptionDetails?.expirationDate).getTime() <
      new Date().getTime()
  );
  useEffect(() => {
    setIsExpired(
      new Date(selectedTeamSubscriptionDetails?.expirationDate).getTime() <
        new Date().getTime()
    );
  }, [selectedTeam, selectedTeamSubscriptionDetails]);

  const teamPersonal = team?.personal ?? false;

  const displayedLockCreateTeam =
    !teamPersonal && isExpired && (team?.hasReachedMaxProjects ?? false);

  const isActiveOrTrialingTeam =
    selectedTeamSubscriptionDetails?.subscriptionPlanName === 'Team' &&
    (selectedTeamSubscriptionDetails?.subscriptionStatus === 'trialing' ||
      selectedTeamSubscriptionDetails?.subscriptionStatus === 'active');

  const projectLeft =
    (selectedTeam?.maxNumberOfProjects ?? 0) -
    (selectedTeam?.numberOfProjectsUsed ?? 0);

  // Only disable if team is canceled/expired AND project limit NOT reached
  // (if limit is reached, paywall will show instead)
  const shouldProjectCreateDisabled =
    team?.subscriptionDetails?.isSubscriptionCanceled &&
    new Date(team?.subscriptionDetails?.expirationDate).getTime() <
      new Date().getTime() &&
    !(team?.hasReachedMaxProjects ?? false);

  const personalProjectTeam = teams.filter((i) => i.personal);
  const personalTeam = personalProjectTeam?.[0];
  const hasReachedLimit = personalTeam?.hasReachedMaxProjects;
  // Disable personal team selection when at project limit — tooltip shown via SelectModalContent
  const disablePersonalProjects = !!hasReachedLimit;

  const usersTeam = () => {
    if (!me) {
      return [];
    }
    const owner = {
      id: me.id,
      avatar: me.avatar,
      name: me.name,
      firstName: me.firstName,
      lastName: me.lastName,
      role: ProjectMemberRole.Owner,
    };
    return [owner];
  };
  const initialValues: FormValues = {
    users: usersTeam(),
    teamId: null,
    plan: '',
    planName: '',
    billingCycle: 'year',
    paymentComplete: false,
    priceId: undefined,
    price: 0,
    location: null,
    name: '',
    skillIds: [],
    logo: null,
  };

  const submitFormTeam = async (
    values: TeamFormValues,
    setSubmitting: (a: boolean) => void
  ) => {
    setSubmitting(true);

    try {
      const { name, location, skillIds, logo } = values;
      let createTeamResult;

      // Only proceed if name is provided as it is required.
      if (name) {
        const avatarBlobId = await uploadTeamLogo(
          logo as LocalFile | null,
          processFiles
        );
        const attributes = {
          name,
          skillIds: skillIds ?? [],
          address: location?.address,
          latitude: location?.latitude,
          longitude: location?.longitude,
          avatarBlobId,
        };

        createTeamResult = await createTeam({
          variables: {
            attributes,
          },
        });
      }

      // After successful team creation, handle UI updates
      setIsTaskWebPanelOpen(false);
      setIsTaskWebPanelMaximize(false);
      setIdTaskDetailOpen('');
      setIdProjectOfTaskDetailOpen('');
      setIsCreateProjectOpen(false);
      setEditProjectUUId('');
      setUpgradeTeamPlanOpen(true);

      if (createTeamResult?.data?.createTeam) {
        setIdTeamOpen(createTeamResult.data.createTeam.id);
        setUUIdTeamOpen(createTeamResult.data.createTeam.uuid);
        setIsNewTeam(true);
      } else {
        setIdTeamOpen('');
        setUUIdTeamOpen('');
        setIsNewTeam(false);
      }

      setIsTeamPersonal(false);
      setTeamValues(values);
    } catch (error) {
      Sentry.captureException(
        new Error('Form submission failed: ' + JSON.stringify(error))
      );
    } finally {
      setSubmitting(false);
    }
  };

  const createButton = () => {
    const disabled = isSubmitting || (!projectId && loading);
    const bgColor = isHovered ? 'grey05' : 'black';

    return (
      <Button
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        isSmall
        disabled={disabled}
        isLoading={isSubmitting}
        backgroundColor={disabled ? undefined : bgColor}
        borderColor={disabled ? undefined : bgColor}
        variant='webprimary'
        marginTop='l'
        accessibilityLabel={
          projectId
            ? `${t('models:projects.update.title')} button`
            : `${t('models:projects.create.title')} button`
        }
        onPress={async () => {
          setNameSubmitAttempted(true);
          const nameErrors = await setFieldTouched('name', true, true);
          if (nameErrors?.name) {
            return;
          }
          const teamErrors = await setFieldTouched('teamId', true, true);
          if (teamErrors?.teamId) {
            return;
          }

          // Check paywall first when creating a project for the selected team (applies to all roles)
          if (
            !projectId &&
            selectedTeamWithBilling &&
            shouldShowPaywall(selectedTeamWithBilling)
          ) {
            setPaywallTeam(selectedTeamWithBilling);
            setPaywallModalOpen(true);
            return;
          }
          // Personal team at project limit — show paywall modal
          if (
            !projectId &&
            selectedTeam &&
            hasReachedPersonalProjectLimit(selectedTeam)
          ) {
            setPaywallTeam(selectedTeam);
            setPaywallModalOpen(true);
            setIsCreateProjectOpen(false);
            setEditProjectUUId('');
            return;
          }
          // Non-personal team: expired subscription with project limit
          if (!projectId && displayedLockCreateTeam) {
            toast.show(
              <CustomToast
                onPress={() => {
                  navigation.navigate('subscription-plans');
                  setIsCreateProjectOpen(false);
                  setEditProjectUUId('');
                  setIsTaskWebPanelOpen(false);
                  setIsTaskWebPanelMaximize(false);
                  setIdTaskDetailOpen('');
                  setIdProjectOfTaskDetailOpen('');
                  setUpgradeCTA(true);
                }}
                redirectTo='my-account'
                params={{
                  screen: 'create-subscription',
                  params: {
                    teamUUId: team?.uuid,
                    isPersonal: teamPersonal,
                    isPlanPlus: !!teamPersonal,
                    planId: teamPersonal
                      ? getProductPlus?.id
                      : getProductTeam?.id,
                  },
                }}
                isTryFree
              />,
              {
                type: 'custom',
                placement: 'bottom',
                duration: 10000,
                animationType: 'zoom-in',
              }
            );
            setIsCancelModalVisible(false);
            setIsCreateProjectOpen(false);
            setEditProjectUUId('');
            return;
          }
          setOverrideSaveAlert(true);
          submitFormIgnoringRejection(submitForm);
          removeProjectLimitSnackBarNextTime(me?.id, '0');
          removeProjectLimitSnackBarNextTime(me?.id, selectedTeam?.id);
        }}>
        {projectId
          ? t('models:projects.update.title')
          : t('models:projects.create.title')}
      </Button>
    );
  };

  const teamIcon =
    selectedTeamName || currentTeam?.name ? (
      <Icon name='Logo' variant='l' color='white' disabled={false} />
    ) : undefined;

  useEffect(() => {
    selectedMembers && setFieldValue('users', selectedMembers);
  }, [selectedMembers]);

  useEffect(() => {
    setSelectedMembers(projectId ? users : []);
    return () => setSelectedMembers([]);
  }, []);

  useEffect(() => {
    if (
      !projectId &&
      !isActiveOrTrialingTeam &&
      selectedTeam?.maxNumberOfProjects
    ) {
      if (projectLeft < 1 && selectedTeam?.maxNumberOfProjects > 0) {
        setShowProjectLimitSnackBar(true);
      } else if (
        projectLeft === 1 ||
        (projectLeft === 2 && selectedTeam?.maxNumberOfProjects > 0)
      ) {
        needShowProjectLimitSnackBar();
      } else {
        setShowProjectLimitSnackBar(false);
      }
    }
  }, [selectedTeam]);

  const needShowProjectLimitSnackBar = async () => {
    const savedTimes = await getShowProjectLimitSnackBarNextTime();
    const savedTime = savedTimes?.find(
      (item) => item.userId === me?.id && item.teamId === selectedTeam?.id
    );
    if (savedTime) {
      if (dayjs(Date()).diff(new Date(savedTime.nextTime)) < 0) {
        return;
      }
    }
    setShowProjectLimitSnackBar(true);
  };

  const goToUpgradeSubscription = (team: Team) => {
    setIsTaskWebPanelOpen(false);
    setIsTaskWebPanelMaximize(false);
    setIdTaskDetailOpen('');
    setIdProjectOfTaskDetailOpen('');
    setIsCreateProjectOpen(false);
    setEditProjectUUId('');
    setIdTeamOpen(team?.id ?? '');
    setUUIdTeamOpen(team?.uuid ?? '');
    setIsTeamPersonal(team?.personal ?? false);
    setTeamValues(team);
    setUpgradeConfirmModalOpen(true);
  };

  return (
    <>
      <Box flex={1} backgroundColor='white' maxWidth={600}>
        <Box flex={1}>
          <ScrollView>
            {!projectId &&
              !(showOverlay && projectTutorialWebState?.PROJECT !== 'DONE') && (
                <Box
                  borderBottomColor='grey02'
                  borderBottomWidth={1}
                  marginBottom='l'>
                  <ListNavigationItem
                    title='Copy from existing project'
                    iconName='Copy'
                    // project-copy-search sits in project-form-stack for web. In mobile, it is in project-stack
                    onPress={() => navigation.navigate('project-copy-search')}
                    hideChevron={true}
                  />
                </Box>
              )}
            <TooltipOnboardingWeb
              variant='left-center'
              tooltipStyle='success'
              title={t('models:projects.tooltip.nameTitle')}
              description={t('models:projects.tooltip.nameSubtitle')}
              open={showNameTooltip}
              forceShow={showNameTooltip}
              animatedOpacity={tooltipOpacity}
              style={{ alignSelf: 'stretch' }}
              triggerAnchorHeight={98}>
              <Field
                component={TextField}
                innerRef={nameRef}
                label='Name'
                name='name'
                hideError={!nameSubmitAttempted}
                accessible={true}
                isRequired
                placeholder={t('models:projects.placeholders.nameForWeb')}
                textInputProps={{ maxLength: PROJECT_NAME_LENGTH_LIMIT }}
                showCharsRemaining
                accessibilityLabel={t(
                  'models:projects.placeholders.nameForWeb'
                )}
                needToShowTailWhenSingleLine={true}
              />
            </TooltipOnboardingWeb>

            <EditTaskDescription
              disabled={isSubmitting}
              maxLength={PROJECT_DESC_LENGTH_LIMIT}
              placeholder={t('models:projects.placeholders.description')}
              hideBorder
              blurOnEnter
              hideScrollbar
            />

            <Text color='textPrimary' variant='labelSmall'>
              {t('shared:address')}
            </Text>
            <TouchableOpacity
              onPress={() => navigation.navigate('project-location-search')}>
              <Box
                accessibilityLabel='Address'
                flexDirection='row'
                borderColor='grey02'
                borderWidth={1}
                height={48}
                alignItems='center'
                borderRadius='xs'
                paddingHorizontal='m'
                mt='xs'
                mb='m'>
                <Icon name='MapPin' variant='m' color='black' />
                {location && (
                  <Text
                    variant='bodySecondary'
                    color='black'
                    numberOfLines={1}
                    mr='m'
                    ml='xs'>
                    {location.address}
                  </Text>
                )}
              </Box>
            </TouchableOpacity>

            <Box flexDirection='row' mb='m'>
              <Box flex={1}>
                <Box
                  flexDirection='row'
                  alignItems='flex-end'
                  justifyContent='center'>
                  <Text marginBottom='xs' variant='bodyTaskName'>
                    {t('models:projects.create.teams.team')}
                  </Text>
                  <Box flex={1}></Box>
                  <Text marginBottom='xs' variant='labelRequired'>
                    {t('models:projects.create.required')}
                  </Text>
                </Box>

                <Button
                  variant='edit'
                  fullWidth
                  isSmall
                  onPress={() => {
                    navigator.navigate('team-select', {
                      disablePersonalProjects,
                    });
                    setTimeout(() => {
                      setTouchedTeam(true);
                    }, 500);
                  }}
                  prefix={
                    <Box
                      width={24}
                      height={24}
                      alignItems='center'
                      justifyContent='center'
                      backgroundColor={
                        selectedTeam?.personal ? 'greenSecondary' : undefined
                      }
                      borderRadius='xxs'
                      marginRight='m'>
                      {selectedTeam?.personal ? (
                        <Icon
                          name='Office'
                          variant='s'
                          color='white'
                          disabled={false}
                        />
                      ) : (
                        teamIcon
                      )}
                    </Box>
                  }
                  accessibilityLabel='Assign Team'>
                  <Text
                    variant='bodyTaskName'
                    color={
                      selectedTeamName || currentTeam?.name
                        ? 'textPrimary'
                        : 'grey04'
                    }>
                    {selectedTeamName || currentTeam?.name || 'Assign Team'}
                  </Text>
                </Button>

                {shouldProjectCreateDisabled && (
                  <Box style={styles.container}>
                    <Box style={styles.warningContainer}>
                      <Icon
                        variant='m'
                        name='AlertTriangle'
                        color='alertRed'
                        marginEnd='xxs'
                      />
                      <Text variant='labelSmall' color='alertRed'>
                        Trial Expired
                      </Text>
                    </Box>
                    {isOwnerOfTeam && (
                      <TouchableOpacity
                        onPress={() => {
                          goToUpgradeSubscription(selectedTeam);
                        }}>
                        <Text
                          variant='labelSmall'
                          color='greenSecondary'
                          textDecorationLine='underline'>
                          Upgrade Now
                        </Text>
                      </TouchableOpacity>
                    )}
                  </Box>
                )}

                {touchedTeam && !(selectedTeamName || currentTeam?.name) && (
                  <Box flexDirection='row' alignItems='center' marginBottom='s'>
                    <Icon
                      name='AlertTriangle'
                      color='alertRed'
                      variant='s'
                      marginRight='xxs'
                    />
                    <Text variant='error'>
                      {t('models:projects.create.teams.teamIsRequired')}
                    </Text>
                  </Box>
                )}
              </Box>
              <Box marginLeft='m'>
                <Text marginBottom='xs' variant='bodyTaskName'>
                  {t('models:projects.color')}
                </Text>
                <TouchableOpacity
                  accessibilityLabel='Color'
                  // @ts-expect-error web-only mouse handlers on Touchable
                  onMouseEnter={() => setIsColorHovered(true)}
                  onMouseLeave={() => setIsColorHovered(false)}
                  style={{
                    width: 72,
                    height: 48,
                    paddingHorizontal: theme.spacing.l,
                    paddingVertical: theme.spacing.s,
                    borderRadius: theme.spacing.xs,
                    borderWidth: 1,
                    borderColor: theme.colors.grey03,
                    backgroundColor: isColorHovered
                      ? theme.colors.grey02
                      : theme.colors.grey01,
                  }}
                  onPress={() => navigator.navigate('color-select')}>
                  {selectedColor ? (
                    <View
                      style={{
                        backgroundColor: selectedColor.value,
                        borderRadius: 24,
                        width: 24,
                        height: 24,
                        marginRight: theme.spacing.xxs,
                      }}></View>
                  ) : undefined}
                </TouchableOpacity>
              </Box>
              <Box marginLeft='m'>
                <Text marginBottom='xs' variant='bodyTaskName'>
                  {t('models:projects.icon')}
                </Text>
                <TouchableOpacity
                  accessibilityLabel='Icon'
                  // @ts-expect-error web-only mouse handlers on Touchable
                  onMouseEnter={() => setIsIconHovered(true)}
                  onMouseLeave={() => setIsIconHovered(false)}
                  style={{
                    width: 72,
                    height: 48,
                    paddingHorizontal: theme.spacing.l,
                    paddingVertical: theme.spacing.s,
                    borderRadius: theme.spacing.xs,
                    borderWidth: 1,
                    borderColor: theme.colors.grey03,
                    backgroundColor: isIconHovered
                      ? theme.colors.grey02
                      : theme.colors.grey01,
                  }}
                  onPress={() => navigator.navigate('icon-select')}>
                  {icon ? (
                    <Icon
                      name={icon as keyof typeof Images}
                      variant='l'
                      color='textPrimary'
                    />
                  ) : undefined}
                </TouchableOpacity>
              </Box>
            </Box>

            <AddMembersSection
              isCreateProject={!projectId}
              isEditProject={!!projectId}
              members={sortedUsers}
              updateMember={(member, role) => updateMember(member.id, role)}
              removeMember={(member) => {
                const members = users?.filter((u) => u.id !== member.id);
                setFieldTouched('users');
                setFieldValue('users', [...members]);
                setSelectedMembers(members);
              }}
              addMembers={() => {
                setFieldTouched('users', true);
                setShowAddMembersModal(true);
              }}
            />
          </ScrollView>
        </Box>
        <Box flexDirection='row'>
          <Box flex={1}>
            <Button
              onMouseEnter={() => setIsCancelHovered(true)}
              onMouseLeave={() => setIsCancelHovered(false)}
              isSmall
              marginTop='l'
              marginRight='xs'
              borderWidth={1}
              backgroundColor={isCancelHovered ? 'grey02' : 'white'}
              borderColor={isCancelHovered ? 'grey02' : 'black'}
              borderRadius='xs'
              variant='textweb'
              onPress={() => {
                // TA-10949 #24: only confirm when there's something to lose.
                if (isMeaningfullyDirty) {
                  setIsCancelModalVisible(true);
                } else {
                  onPressCancel();
                }
              }}
              float='bottom'
              accessibilityLabel={t('shared:cancel')}>
              {t('shared:cancel')}
            </Button>
          </Box>
          <Box flex={1}>{createButton()}</Box>
        </Box>

        <CancelConfirmTaskAndProjectModal
          showModal={isCancelModalVisible}
          onClose={() => {
            setIsCancelModalVisible(false);
          }}
          onPress={onPressCancel}
          buttonTextOne={
            screenName == 'Create'
              ? t('models:tasks.cancel.btnCreateText')
              : t('models:tasks.cancel.btnEditText')
          }
          buttonText={
            screenName == 'Create'
              ? t('models:tasks.cancel.removeProjectBtnText')
              : t('models:tasks.cancel.btnCancelText')
          }
          title={
            screenName == 'Create'
              ? t('models:tasks.cancel.projectCancelTitle')
              : t('models:tasks.cancel.cancelEditingTitle')
          }
          message={
            screenName == 'Create'
              ? t('models:tasks.cancel.projectCreateMessage')
              : t('models:tasks.cancel.projectEditMessage')
          }
        />
      </Box>
      {showCreateTeamModal && (
        <Formik
          initialValues={initialValues}
          validationSchema={createTeamSchema}
          onSubmit={(values, { setSubmitting }) => {
            // Use selectedTeam from teams list merged with its billing data
            if (shouldShowPaywall(selectedTeamWithBilling)) {
              setPaywallTeam(selectedTeamWithBilling ?? undefined);
              setPaywallModalOpen(true);
              setSubmitting(false);
              return;
            }
            submitFormTeam(values, setSubmitting);
          }}>
          {() => (
            <TeamsModal
              title='Team Setup'
              buttonLabel={t('shared:next')}
              onClose={() => setShowCreateTeamModal(false)}
            />
          )}
        </Formik>
      )}
      <WebModalV3
        canOverflowModal={true}
        accessibilityLabel='Add Project Member'
        onClose={() => setShowAddMembersModal(false)}
        visible={showAddMembersModal}
        width={800}
        height={412}>
        <ProjectInviteOrAddMember
          key={`invite-modal-${users.length}-${users
            .map((u) => `${u.id}-${u.role}`)
            .join(',')}`}
          projectId={projectId}
          canAssignAdminRole={
            !projectId ||
            canUpdateAdminRole(
              users.find((u) => u.id === me?.id)?.role,
              team?.currentUserRole
            )
          }
          selectedUser={projectId ? [] : sortedUsers}
          existingProjectMembers={projectId ? sortedUsers : undefined}
          onAddEmailInvitations={(invitations, selectedUsers) => {
            if (!projectId) {
              setFieldTouched('projectMembers');
              setFieldValue('projectMembers', invitations);
            }

            // Create a map of email to full user data for quick lookup
            // This preserves avatar, name, etc. for existing users
            const userDataByEmail = new Map<string, any>();
            selectedUsers?.forEach((u: any) => {
              if (u?.email) {
                userDataByEmail.set(u.email.toLowerCase(), u);
              }
            });

            // For EDIT projects, update roles of existing members instead of adding duplicates
            // For CREATE projects, keep only owner + new invitations
            let updatedMembers;
            if (projectId) {
              // EDIT project: Update roles of existing members that match
              // invitation emails, keep non-matching members unchanged, and
              // add new invitations for emails that don't match any
              // existing member. See resolveEditModeMembers for the
              // case-insensitive matching this relies on.
              updatedMembers = resolveEditModeMembers(
                users as Parameters<typeof resolveEditModeMembers>[0],
                invitations,
                userDataByEmail
              );
            } else {
              // CREATE project: Keep only owner + new invitations
              const existingOwner = users.find(
                (u) => u.role === ProjectMemberRole.Owner
              );
              // Use full user data if available (preserves avatar, name)
              const invitedMembers = invitations.map((inv, index) => {
                const fullUserData = userDataByEmail.get(
                  inv.email.toLowerCase()
                );
                if (fullUserData) {
                  // Use full user data with updated role
                  return {
                    id: fullUserData.id || `invited-${index}-${inv.email}`,
                    name: fullUserData.name || inv.email,
                    firstName: fullUserData.firstName,
                    lastName: fullUserData.lastName,
                    email: inv.email,
                    avatar: fullUserData.avatar,
                    role: inv.role,
                    __typename: fullUserData.__typename || 'User',
                  };
                }
                // Fallback for email-only invitations
                return {
                  id: `invited-${index}-${inv.email}`,
                  name: inv.email,
                  email: inv.email,
                  role: inv.role,
                  __typename: 'InvitedUser',
                };
              });
              updatedMembers = existingOwner
                ? [existingOwner, ...invitedMembers]
                : invitedMembers;
            }

            setFieldTouched('users');
            setFieldValue('users', updatedMembers);
            setSelectedMembers(updatedMembers);
            setShowAddMembersModal(false);
          }}
          onClose={() => setShowAddMembersModal(false)}
        />
      </WebModalV3>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: theme.spacing.s,
    backgroundColor: 'white',
    borderRadius: 4,
  },
  warningContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  warningIcon: {
    marginRight: 5,
    fontSize: 16,
  },
  upgradeButton: {
    color: '#4caf50',
    fontWeight: '500',
    fontSize: 14,
  },
});

export default ProjectForm;
